import { useEffect } from "react";
import { useActiveCanvas, useActiveMainCanvas } from "@/utils/utils";
import { EMPTY_TREE_CODEBASE_ID } from "@/utils/constants";
import _ from "lodash";

import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { usePruneInactiveStoryboards } from "@/hooks/storyboards/usePruneInactiveStoryboards";
import { useTailwindClasses } from "@/hooks/useTailwindClasses";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

const MAX_NUM_LIVE_STORYBOARDS = 4;

const getCodebaseIdsFromHtml = (html: string) => {
  const codebaseIdRegex =
    /tempo-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const codebaseIdMatches = html.match(codebaseIdRegex) || [];
  const codebaseIds = new Set<string>(codebaseIdMatches);
  return codebaseIds;
};

export const CanvasSideEffect = ({
  storyboardIdToSetHTML,
  setStoryboardIdToSetHTML,
}) => {
  const storyboardToNavTree = useCanvasStore(
    (state) => state.storyboardToNavTree
  );

  const mainCanvas = useActiveMainCanvas();
  const activeCanvas = useActiveCanvas();

  useTailwindClasses();

  // Storyboard online/offline status
  const storyboardIdToFullHTML = useCanvasStore(
    (store) => store.storyboardIdToFullHTML
  );
  const storyboardIdToStatus = useCanvasStore(
    (store) => store.storyboardIdToStatus
  );
  const setStoryboardIdToStatus = useCanvasStore(
    (store) => store.setStoryboardIdToStatus
  );
  const setFullHtmlForStoryboard = useCanvasStore(
    (store) => store.setFullHtmlForStoryboard
  );
  const setStoryboardIdToCodebaseIds = useCanvasStore(
    (store) => store.setStoryboardIdToCodebaseIds
  );
  const savedStoryboardHtml = useSupabaseStore(
    (store) => store.savedStoryboardHtml
  );
  const savedStoryboardHtmlStatus = useSupabaseStore(
    (store) => store.savedStoryboardHtmlStatus
  );

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );
  const onDbSnapshot = useSupabaseStore((store) => store.onDbSnapshot);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  /**
   * Clear the storyboard to instant update HTML when the iframe loads
   */
  useEffect(() => {
    const storyboardsLoaded = [];
    Object.keys(storyboardIdToSetHTML || {}).forEach((storyboardId) => {
      if (storyboardToNavTree?.[storyboardId]) {
        // @ts-expect-error TS2345
        storyboardsLoaded.push(storyboardId);
      }
    });

    if (storyboardsLoaded.length) {
      const newStoryboardToSetHTML = { ...storyboardIdToSetHTML };
      storyboardsLoaded.forEach((storyboardId) => {
        delete newStoryboardToSetHTML[storyboardId];
      });
      setStoryboardIdToSetHTML(newStoryboardToSetHTML);
    }
  }, [storyboardToNavTree]);

  const storyboardsLoaded = Boolean(activeCanvas?.storyboards);

  /**
   * On first load, set some storyboars to frozen right away if their HTML is
   * in supabae
   *
   * Also do the same for sub canvases when the component key is set
   */
  useEffect(() => {
    if (
      !storyboardsLoaded ||
      !supabaseClient ||
      !activeCanvas ||
      savedStoryboardHtmlStatus !== "succeeded"
    ) {
      return;
    }

    const checkSupabaseAndFreeze = async () => {
      const storyboardIdToSavedHtml = {};
      Object.values(savedStoryboardHtml)?.forEach((savedHtmlObj) => {
        storyboardIdToSavedHtml[savedHtmlObj.storyboard_id] = savedHtmlObj;
      });

      // Prioritize
      const storyboardsCanBeFrozen = activeCanvas?.storyboards
        ?.filter(
          (s) =>
            storyboardIdToSavedHtml[s.id]?.html &&
            storyboardIdToSavedHtml[s.id]?.last_frozen_at
        )
        .sort((a, b) => {
          const updatedAtA = new Date(
            storyboardIdToSavedHtml[a.id]?.last_frozen_at
          );
          const updatedAtB = new Date(
            storyboardIdToSavedHtml[b.id]?.last_frozen_at
          );

          return updatedAtA.getTime() - updatedAtB.getTime();
        });

      // Freeze all storyboard on the canvas when it first loads
      storyboardsCanBeFrozen.forEach(async (sb) => {
        const html: string = storyboardIdToSavedHtml[sb.id].html;
        setFullHtmlForStoryboard(sb.id, html);
        setStoryboardIdToStatus(sb.id, {
          status: "frozen",
          last_updated: new Date(),
        });

        // Parse out all codebase ids in the html and set that to the storyboard id
        const codebaseIds = getCodebaseIdsFromHtml(html);
        setStoryboardIdToCodebaseIds(sb.id, codebaseIds);
      });
    };

    checkSupabaseAndFreeze();
  }, [
    storyboardsLoaded,
    supabaseClient,
    canvasComponentKey,
    savedStoryboardHtmlStatus,
  ]);

  /**
   * When the saved storyboard html first loads, parse out the codebase ids from it for all storyboards
   */
  useEffect(() => {
    if (!supabaseClient || savedStoryboardHtmlStatus !== "succeeded") {
      return;
    }

    Object.values(savedStoryboardHtml).forEach((savedHtmlObj) => {
      const html = savedHtmlObj.html;
      if (!html) {
        return;
      }

      // Parse out all codebase ids in the html and set that to the storyboard id
      const codebaseIds = getCodebaseIdsFromHtml(html);
      setStoryboardIdToCodebaseIds(savedHtmlObj.storyboard_id, codebaseIds);
    });
  }, [supabaseClient, savedStoryboardHtmlStatus]);

  /**
   * When a storyboard goes live, manage iframe reuse instead of freezing
   */
  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    const storyboardFullyLoaded = (sbId: string) =>
      storyboardIdToFullHTML[sbId] &&
      storyboardToNavTree?.[sbId] &&
      storyboardToNavTree?.[sbId]?.tempoElement.codebaseId !==
        EMPTY_TREE_CODEBASE_ID;

    // In descending order
    const liveStoryboards = Object.keys(storyboardIdToStatus)
      .map((sbId) => ({
        sbId,
        ...storyboardIdToStatus[sbId],
      }))
      .filter((s) => s.status === "live" && storyboardFullyLoaded(s.sbId))
      .sort((a, b) => b.last_updated.getTime() - a.last_updated.getTime());

    const storyboardsToFreeze = liveStoryboards.slice(MAX_NUM_LIVE_STORYBOARDS);

    // Use bulk update for iframe reuse logic
    if (storyboardsToFreeze.length > 0) {
      const statusUpdates = {};
      storyboardsToFreeze.forEach((sb) => {
        statusUpdates[sb.sbId] = {
          status: "frozen" as const,
          last_updated: new Date(),
        };
      });

      // Use bulk update which will handle iframe reuse logic
      useCanvasStore.getState().bulkSetStoryboardIdToStatus(statusUpdates);
    }

    // Always save all the html for all live storyboards
    const newItems: any[] = [];
    liveStoryboards.forEach(async (sb) => {
      const newItem = {
        storyboard_id: sb.sbId,
        html: storyboardIdToFullHTML[sb.sbId],
        canvas_id: mainCanvas.id,
        last_frozen_at: new Date().toISOString(), // Convert to ISO string
      };

      await supabaseClient
        .from("saved_storyboard_html")
        .upsert(newItem, { onConflict: "storyboard_id" })
        .eq("storyboard_id", sb.sbId);

      newItems.push(newItem);
    });

    if (newItems.length) {
      onDbSnapshot(
        "savedStoryboardHtml",
        {
          newItems,
        },
        undefined,
        "storyboard_id"
      );
    }
  }, [storyboardIdToStatus, storyboardIdToFullHTML, storyboardToNavTree]);

  usePruneInactiveStoryboards();
  return <></>;
};
